import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Icon from '../components/Icon';
import LoadError from '../components/LoadError';
import ImageLightbox from '../components/ImageLightbox';
import { OrderTimeline, fmtTimeSlot } from '../components/OrderTimeline';
import { DetailCard, DetailRow, ImageThumb, cap, fmtDate, fmtDateTime } from '../components/DetailParts';
import { useToast } from '../components/Toast';
import {
  ADMIN_SETTABLE_ORDER_STATUSES,
  OrderStatus,
  ServiceOrder,
  extractApiError,
  getOrder,
  photoSrc,
  statusBadgeClass,
  statusLabel,
  updateOrderStatus,
} from '../api/orders';

/**
 * A maintenance service order (Service Order page → View): what the customer
 * filled in — service, urgency, preferred date and time slot, description,
 * photos — plus its progress. Payment isn't shown for requests.
 */
const ServiceRequestDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [order, setOrder] = useState<ServiceOrder | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<boolean>(false);
  // Index of the photo open in the viewer, or null.
  const [viewing, setViewing] = useState<number | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      setOrder(await getOrder(Number(id)));
    } catch (err) {
      setError(extractApiError(err, 'Failed to load order'));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const changeStatus = async (next: OrderStatus) => {
    if (!order || next === order.status) return;
    setSaving(true);
    try {
      const updated = await updateOrderStatus(order.id, next);
      setOrder((o) => (o ? { ...o, ...updated } : o));
      toast(`${order.orderRef || 'Request'} → ${statusLabel(next)}`, 'success');
    } catch (err) {
      toast(extractApiError(err, 'Failed to update status'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const backToList = () => navigate('/admin/service-requests');

  if (loading) {
    return (
      <div className="py-20 text-center text-g500">
        <Icon name="wrench" size={28} color="var(--g400)" /> Loading order...
      </div>
    );
  }

  if (error || !order) {
    return (
      <LoadError
        noun="order"
        error={error}
        onRetry={load}
        backLabel="Back to service orders"
        onBack={backToList}
      />
    );
  }

  const photos = (Array.isArray(order.photos) ? order.photos : []).map(photoSrc);
  const timeline = Array.isArray(order.timeline) ? order.timeline : [];

  return (
    <div className="animate-fadeIn">
      {/* Top bar */}
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <button
          className="flex items-center gap-2 text-sm font-semibold text-g600 hover:text-ink transition-colors"
          onClick={backToList}
        >
          <Icon name="arrow-left" size={18} /> Back to service orders
        </button>
        <div className="flex items-center gap-2.5">
          <span className="text-[13px] text-g500">Update status</span>
          <select
            value={order.status}
            disabled={saving}
            onChange={(e) => changeStatus(e.target.value as OrderStatus)}
            className="form-input"
            style={{ width: 170, padding: '7px 10px', fontWeight: 600, cursor: saving ? 'wait' : 'pointer' }}
          >
            {/* Keep the current status listed even when admin can't set it. */}
            {!ADMIN_SETTABLE_ORDER_STATUSES.includes(order.status) && (
              <option value={order.status} disabled>
                {statusLabel(order.status)}
              </option>
            )}
            {ADMIN_SETTABLE_ORDER_STATUSES.map((s) => (
              <option key={s} value={s}>
                {statusLabel(s)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Heading */}
      <div className="card p-6 mb-5 flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="text-[12px] text-g500 font-semibold uppercase tracking-wider">Service order</div>
          <div className="text-2xl font-extrabold text-ink">{order.orderRef || `#${order.id}`}</div>
          <div className="text-[13px] text-g500 mt-0.5">Submitted {fmtDateTime(order.createdAt)}</div>
        </div>
        <div className="flex items-center gap-3">
          {order.urgency === 'urgent' && <span className="badge badge-rejected">Urgent</span>}
          <span className={statusBadgeClass(order.status)}>{statusLabel(order.status)}</span>
        </div>
      </div>

      <div className="grid gap-5 lg:grid-cols-2 items-start">
        <div className="flex flex-col gap-5">
          <DetailCard icon="wrench" title="Request">
            {/* A maintenance request stores the same text in serviceName and category. */}
            <DetailRow label="Service">{order.serviceName || order.category || '—'}</DetailRow>
            <DetailRow label="Urgency">
              {order.urgency ? (
                <span
                  style={{
                    padding: '2px 10px',
                    borderRadius: 999,
                    fontSize: 12,
                    fontWeight: 700,
                    background: order.urgency === 'urgent' ? '#FEE2E2' : '#DBEAFE',
                    color: order.urgency === 'urgent' ? '#DC2626' : '#2563EB',
                  }}
                >
                  {cap(order.urgency)}
                </span>
              ) : (
                '—'
              )}
            </DetailRow>
            <DetailRow label="Preferred date">{fmtDate(order.preferredDate)}</DetailRow>
            <DetailRow label="Time slot">{fmtTimeSlot(order.timeSlot)}</DetailRow>
          </DetailCard>

          <DetailCard icon="user" title="Customer">
            <DetailRow label="Name">
              {order.customer?.name ? (
                <button
                  type="button"
                  className="text-brand hover:underline font-semibold text-right"
                  onClick={() => navigate(`/admin/users/${order.userId}`)}
                >
                  {order.customer.name}
                </button>
              ) : (
                '—'
              )}
            </DetailRow>
            <DetailRow label="Email">
              {order.customer?.email ? (
                <a href={`mailto:${order.customer.email}`} className="hover:text-brand">
                  {order.customer.email}
                </a>
              ) : (
                '—'
              )}
            </DetailRow>
          </DetailCard>

          <DetailCard icon="message-square" title="Description">
            {order.description ? (
              <div
                className="text-sm text-g700 leading-relaxed"
                style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}
              >
                {order.description}
              </div>
            ) : (
              <span className="text-sm text-g500">No description provided.</span>
            )}
          </DetailCard>

          <DetailCard icon="file-text" title={`Photos (${photos.length})`}>
            {photos.length ? (
              <div className="flex gap-2.5 flex-wrap">
                {photos.map((p, i) => (
                  <ImageThumb key={i} src={p} alt={`Attachment ${i + 1}`} onOpen={() => setViewing(i)} />
                ))}
              </div>
            ) : (
              <span className="text-sm text-g500">Nothing attached.</span>
            )}
          </DetailCard>
        </div>

        <DetailCard icon="calendar" title="Progress">
          <OrderTimeline timeline={timeline} />
          <div className="text-[12px] text-g400 mt-3 pt-3 border-t border-g100">
            Last updated {fmtDateTime(order.updatedAt)}
          </div>
        </DetailCard>
      </div>

      <ImageLightbox
        src={viewing === null ? null : photos[viewing] ?? null}
        onClose={() => setViewing(null)}
        title={`Service order ${order.orderRef || '#' + order.id}`}
      />
    </div>
  );
};

export default ServiceRequestDetail;
